import { mainApi } from '../../../../../api/mainApi';
import { middlewareApi } from '../../../../middleware/middlewareApi';
import { EvaluationStudentDetail } from '../../../../students/virtual-classroom/evaluations/interfaces/evaluationDetail.interfaces';
import { DistributionCourse } from '../interfaces/distributions-courses.interfaces';
import { CategoryActivity } from '../interfaces/evaluation-activity-category.interfaces';
import { EvaluationComment } from '../interfaces/evaluation-comment.interfaces';
import { EvaluationResponse } from '../interfaces/evaluation-response.interfaces';
import { SupplyCourse } from '../interfaces/evaluation-supply.interfaces';
import { Evaluation, EvaluationTeacher } from '../interfaces/evaluations.interfaces';
import { EvaluationDetail } from '../interfaces/evaluation-detail.interfaces';
import { CoursesTeacher } from '../interfaces/teacher-courses';
import {
  setEvaluationToQualify,
  setInfoEvaluation,
  setQuestionsPreloaded,
} from './evaluationSlice';
import { AccessState, AuthState } from '../../../../../types';
import { DetailActivityStudent } from '../../interfaces/DetailActivityStudent';

export const evaluationsTags = middlewareApi.enhanceEndpoints({
  addTagTypes: ['EvaluationsTag'],
});
// Define a service using a base URL and expected endpoints
export const evaluationTeacherApi = evaluationsTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * @GET all evaluations
     */
    getEvaluationsTeacher: builder.query<
      EvaluationResponse,
      {
        userProfileId: number;
        page: number;
        perPage: number;
        courseId?: number;
        supply?: number;
        estado?: string;
      }
    >({
      queryFn: async ({ page, perPage, userProfileId, courseId, supply, estado }) => {
        try {
          const { data } = await mainApi.get<EvaluationResponse>('/evaluations/collaborators', {
            params: {
              user_profile_id: userProfileId,
              page,
              size: perPage,
              sort: 'id',
              type_sort: 'desc',
              course_id: courseId,
              ...(supply && { supply }),
              ...(estado && { estado }),
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((value) => ({ type: 'EvaluationsTag', id: value.id } as const)),
              { type: 'EvaluationsTag', id: 'LIST' },
            ]
          : [{ type: 'EvaluationsTag', id: 'LIST' }],
    }),

    /**
     * @GET get evaluations course detail
     */
    getEvaluationCourse: builder.query<
      EvaluationTeacher,
      { course: number; page: number; perPage: number }
    >({
      queryFn: async ({ page, course, perPage }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<EvaluationTeacher>(`/courses/${course}/evaluations`, {
            params: {
              page,
              perPage,
            },
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET get supply course
     */
    getSupplyCourse: builder.query<SupplyCourse[], { course: number }>({
      queryFn: async ({ course }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;
          const { data } = await mainApi.get<SupplyCourse[]>(`/courses/${course}/supplies`, {
            params: {
              user_profile_id: user_profile_id,
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),
    /**
     * @GET get evaluation detail created
     */
    getEvaluationDetail: builder.query<
      EvaluationDetail,
      { profileId: number; evaluationId: number; courseId: number }
    >({
      queryFn: async ({ profileId, courseId, evaluationId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<EvaluationDetail>(
            `/courses/${courseId}/evaluations/${evaluationId}`,
            {
              params: {
                user_profile_id: profileId,
              },
            }
          );

          //Asignar preguntas iniciales
          const questions = data?.details_activity?.map((e, index) => ({
            id: index,
            question: e?.dta_question,
            catalogId: Number(e?.type_answer_id),
            point: Number(e?.dta_score_evaluated),
            typeQuestion:
              e?.type_answer?.cat_name! === 'Opcion Multiple'
                ? 'multiple'
                : e?.type_answer?.cat_name! === 'Comentario'
                ? 'comentary'
                : 'single',
            options: e?.option_answers?.map((i) => ({
              option: i?.op_ans_description,
              correct: Number(i?.op_ans_is_answer) === 1 ? true : false,
            })),
          }))!;

          dispatch(
            setInfoEvaluation({
              actNumTries: Number(data.act_num_tries),
              description: data.act_description,
              actScoreActivity: Number(data.act_score_activity),
              cactIsExtemporaneous: Number(data.course_activity_one.cact_is_extemporaneous),
              categoryActivityId: Number(data.category_activity_id),
              distributionId: Number(data.calification_models_distributions_id),
              supplyId: Number(data.supply_id),
              endDate: new Date(data.course_activity_one.cact_end_date).getTime(),
              startDate: new Date(data.course_activity_one.cact_start_date).getTime(),
              typeActivityId: Number(data.type_activity_id),

              students: data.course_activity_one.student_activities.map((studen) => {
                return {
                  student: {
                    student_email: studen.student.user.email,
                    student_full_name: studen.student.user.person.pers_first_lastname,
                    student_id: Number(studen.student_id),
                  },
                  grupo: studen.csat_group!,
                };
              }),
            })
          );

          dispatch(setQuestionsPreloaded(questions!));

          return { data: data };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @POST create evaluation course
     */
    createEvaluation: builder.mutation<Evaluation, { course: number; payload: Evaluation }>({
      queryFn: async ({ course, payload }) => {
        try {
          const { data } = await mainApi.post<Evaluation>(`/courses/${course}/evaluations`, {
            ...payload,
          });

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'EvaluationsTag', id: 'LIST' }],
    }),

    /**
     * @DELETE delete evaluation course
     * @param {number} evaluationId
     */

    deleteEvaluation: builder.mutation<Evaluation, { evaluationId: number }>({
      queryFn: async ({ evaluationId }, { dispatch, getState }) => {
        try {
          const {
            access: { currentProfile },
            auth: { userInfo },
          } = getState() as { access: AccessState; auth: AuthState };

          const user_profile_id = currentProfile?.profileId;
          const user_id = userInfo.id;

          const { data } = await mainApi.delete<Evaluation>(
            `/collaborators/evaluations/${evaluationId}`,
            {
              params: {
                user_profile_id,
                user_id,
              },
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'EvaluationsTag', id: 'LIST' }],
    }),

    /**
     * @POST Qualify evaluation teacher
     */
    qualifyEvaluation: builder.mutation<
      Evaluation,
      {
        evaluationId: number;
        studentId: number;
        userId: number;
        userProfileId: number;
        questionId: number;
        payload: { csact_score_evaluate: number; csact_observations: string };
      }
    >({
      queryFn: async ({ evaluationId, studentId, userId, userProfileId, questionId, payload }) => {
        try {
          const { data } = await mainApi.put<Evaluation>(
            `/evaluations/${evaluationId}/students/${studentId}/questions/${questionId}`,
            {
              user_profile_id: userProfileId,
              user_id: userId,
              ...payload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'EvaluationsTag', id: 'LIST' }],
    }),

    /**
     *
     * @Get students evaluations
     */
    getStudentsEvaluations: builder.query<
      DetailActivityStudent,
      { cursoId: number; activityId: number }
    >({
      queryFn: async ({ activityId, cursoId }, { getState }) => {
        try {
          const {
            access: { currentProfile },
          } = getState() as { access: AccessState };

          const user_profile_id = currentProfile?.profileId;

          const { data } = await mainApi.get(
            `/courses/${cursoId}/activities/${activityId}/students`,
            {
              params: {
                user_profile_id,
              },
            }
          );

          return { data: data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'EvaluationsTag', id: 'LIST' }],
    }),

    /**
     * @PUT Comment evaluation
     */
    commentEvaluation: builder.mutation<
      Evaluation,
      { userProfileId: number; courseId: number; evaluationId: number; payload: EvaluationComment }
    >({
      queryFn: async ({ courseId, evaluationId, userProfileId, payload }) => {
        try {
          const { data } = await mainApi.put<Evaluation>(
            `/courses/${courseId}/evaluations/${evaluationId}/collaborators`,
            {
              user_profile_id: userProfileId,
              ...payload,
            }
          );

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: 'EvaluationsTag', id: 'LIST' }],
    }),

    /**
     * @GET Courses availables for teacher in current period
     */
    getCoursesTeacher: builder.query<
      CoursesTeacher[],
      {
        collaboratorId: number;
        periodId: number;
        userProfileId: number;
      }
    >({
      queryFn: async ({ collaboratorId, periodId, userProfileId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<CoursesTeacher[]>(
            `/collaborators/${collaboratorId}/period/${periodId}/course`,
            {
              params: {
                user_profile_id: userProfileId,
                data: 'all',
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET Distributions based in course
     */
    getDistributionsCourseSupply: builder.query<
      DistributionCourse[],
      {
        courseId: number;
        userProfileId: number;
      }
    >({
      queryFn: async ({ courseId, userProfileId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<DistributionCourse[]>(
            `/distributions/courses/${courseId}/supplies`,
            {
              params: {
                user_profile_id: userProfileId,
                data: 'all',
              },
            }
          );

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET Courses availables for teacher
     */
    getCategoriesActivity: builder.query<
      CategoryActivity[],
      {
        typeActivity: string;
        userProfileId: number;
      }
    >({
      queryFn: async ({ typeActivity, userProfileId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<CategoryActivity[]>('/activities/categories', {
            params: {
              user_profile_id: userProfileId,
              type_activity: typeActivity,
            },
          });

          return { data: Array.isArray(data) ? data : [] };
        } catch (error: any) {
          return { error };
        }
      },
    }),

    /**
     * @GET Obtain evaluation to qualify
     */
    getEvaluationStudentToQualifyDetail: builder.query<
      EvaluationStudentDetail,
      {
        evaluationId: number;
        studentId: number;
        userProfileId: number;
        userId: number;
      }
    >({
      queryFn: async ({ userProfileId, userId, evaluationId, studentId }, { dispatch }) => {
        try {
          const { data } = await mainApi.get<EvaluationStudentDetail>(
            `/students/${studentId}/evaluations/${evaluationId}`,
            {
              params: {
                user_profile_id: userProfileId,
                user_id: userId,
              },
            }
          );
          /**
           * Obtener data del array de información de la evaluación sin
           * tener en cuenta la respuesta seleccionada.
           * Preguntas y opciones del array de configuración de la
           * evaluación.
           * answered_activities contiene la información de las preguntas
           * ya resueltas.
           */
          const questionsConfig = data?.details_activity?.map((e) => ({
            id: e.id,
            description: data?.act_description!,
            typeAnswerId: e.type_answer_id,
            question: e.dta_question,
            score: e.dta_score_evaluated,
            isManual: e.is_manual,
            qualified: false,
            urlFile: e.answered_activities[0].csact_url_file,
            califcacion: e.answered_activities[0].csact_score_evaluate,
            comment:
              Number(e.is_manual) === 1
                ? e?.answered_activities.length > 0
                  ? e?.answered_activities[0].csact_comments
                  : null
                : null,
            options: e.option_answers.map((i) => ({
              id: i.id,
              qualify: false,
              description: i.op_ans_description,
              isAnswer: e.answered_activities.find((iter) => Number(iter.option_answer_id) === i.id)
                ? true
                : false,
            })),
          }))!;

          dispatch(setEvaluationToQualify(questionsConfig));

          return { data };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: 'EvaluationsTag', id: 'LIST' }],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetEvaluationsTeacherQuery,
  useGetCoursesTeacherQuery,
  useGetEvaluationDetailQuery,
  useCreateEvaluationMutation,
  useGetCategoriesActivityQuery,
  useCommentEvaluationMutation,
  useGetEvaluationCourseQuery,
  useQualifyEvaluationMutation,
  useGetEvaluationStudentToQualifyDetailQuery,
  useGetDistributionsCourseSupplyQuery,
  useGetSupplyCourseQuery,
  useGetStudentsEvaluationsQuery,
  useDeleteEvaluationMutation,
} = evaluationTeacherApi;
